/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { createServiceToken, getLogger } from "@workglow/util";
import { DataPortSchemaObject, FromSchema, TypedArraySchemaOptions } from "@workglow/util/schema";
import type { ITabularMigrationApplier } from "../migrations";
import { BaseTabularStorage, ClientProvidedKeysOption } from "./BaseTabularStorage";
import { InMemoryTabularStorage } from "./InMemoryTabularStorage";
import {
  AnyTabularStorage,
  AutoGeneratedKeys,
  CoveringIndexQueryOptions,
  DeleteSearchCriteria,
  InsertEntity,
  ITabularStorage,
  QueryOptions,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularSubscribeOptions,
} from "./ITabularStorage";

export const CACHED_TABULAR_REPOSITORY = createServiceToken<AnyTabularStorage>(
  "storage.tabularRepository.cached"
);

/**
 * A tabular repository wrapper that fronts a durable repository with an
 * in-memory cache (defaults to {@link InMemoryTabularStorage}). Durable is the
 * source of truth; the cache is repopulated lazily and on subscribe-driven
 * invalidation.
 */
export class CachedTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType extends InsertEntity<Entity, AutoGeneratedKeys<Schema>> = InsertEntity<
    Entity,
    AutoGeneratedKeys<Schema>
  >,
> extends BaseTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, Value, InsertType> {
  public readonly cache: ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>;
  private durable: ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>;
  private cacheInitialized = false;
  private cacheInitPromise: Promise<void> | null = null;

  constructor(
    durable: ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>,
    cache?: ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>,
    schema?: Schema,
    primaryKeyNames?: PrimaryKeyNames,
    indexes?: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[],
    clientProvidedKeys: ClientProvidedKeysOption = "if-missing"
  ) {
    // Schema and PK can't reliably be reflected off an arbitrary ITabularStorage,
    // so callers must supply them and the wrapper trusts them to match `durable`.
    if (!schema || !primaryKeyNames) {
      throw new Error(
        "Schema and primaryKeyNames must be provided when creating CachedTabularStorage"
      );
    }

    super(schema, primaryKeyNames, indexes || [], clientProvidedKeys);
    this.durable = durable;

    if (cache) {
      this.cache = cache;
    } else {
      this.cache = new InMemoryTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>(
        schema,
        primaryKeyNames,
        indexes || [],
        clientProvidedKeys
      );
    }

    this.setupEventForwarding();
  }

  private setupEventForwarding(): void {
    this.cache.on("put", (entity) => {
      this.events.emit("put", entity);
    });
    this.cache.on("get", (key, entity) => {
      this.events.emit("get", key, entity);
    });
    this.cache.on("query", (key, entities) => {
      this.events.emit("query", key, entities);
    });
    this.cache.on("delete", (key) => {
      this.events.emit("delete", key);
    });
    this.cache.on("clearall", () => {
      this.events.emit("clearall");
    });
  }

  /** Promise-based lock so concurrent callers share one initialization pass. */
  private async initializeCache(): Promise<void> {
    if (this.cacheInitialized) return;

    if (this.cacheInitPromise) {
      return this.cacheInitPromise;
    }

    this.cacheInitPromise = (async () => {
      try {
        const all = await this.durable.getAll();
        if (all && all.length > 0) {
          await this.cache.putBulk(all);
        }
        this.cacheInitialized = true;
      } catch (error) {
        getLogger().warn("Failed to initialize cache from durable repository:", { error });
        // Don't mark as initialized on error — allow retry on next access.
      } finally {
        this.cacheInitPromise = null;
      }
    })();

    return this.cacheInitPromise;
  }

  async put(value: InsertType): Promise<Entity> {
    await this.initializeCache();
    const result = await this.durable.put(value);
    await this.cache.put(result);
    return result;
  }

  async putBulk(values: InsertType[]): Promise<Entity[]> {
    await this.initializeCache();
    const results = await this.durable.putBulk(values);
    await this.cache.putBulk(results);
    return results;
  }

  async get(key: PrimaryKey): Promise<Entity | undefined> {
    await this.initializeCache();
    let result = await this.cache.get(key);
    if (result === undefined) {
      result = await this.durable.get(key);
      if (result) {
        await this.cache.put(result);
      }
    }
    return result;
  }

  async delete(value: PrimaryKey | Entity): Promise<void> {
    await this.initializeCache();
    await this.durable.delete(value);
    await this.cache.delete(value);
  }

  async deleteAll(): Promise<void> {
    await this.initializeCache();
    await this.durable.deleteAll();
    await this.cache.deleteAll();
  }

  async getAll(options?: QueryOptions<Entity>): Promise<Entity[] | undefined> {
    this.validateGetAllOptions(options);
    await this.initializeCache();

    let results = await this.cache.getAll();

    if (!results || results.length === 0) {
      results = await this.durable.getAll();
      if (results && results.length > 0) {
        await this.cache.putBulk(results);
      }
    }

    if (options && results && results.length > 0) {
      return await this.cache.getAll(options);
    }

    return results;
  }

  async size(): Promise<number> {
    await this.initializeCache();
    // Source of truth is durable; cache may lag during invalidation.
    return await this.durable.size();
  }

  async getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined> {
    await this.initializeCache();
    // Delegate to durable to avoid offset drift against a partially-warmed cache.
    return await this.durable.getOffsetPage(offset, limit);
  }

  async query(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined> {
    await this.initializeCache();
    return await this.cache.query(criteria, options);
  }

  override async queryIndex<K extends keyof Entity & string>(
    criteria: SearchCriteria<Entity>,
    options: CoveringIndexQueryOptions<Entity, K>
  ): Promise<Pick<Entity, K>[]> {
    await this.initializeCache();
    return await this.cache.queryIndex(criteria, options);
  }

  async deleteSearch(criteria: DeleteSearchCriteria<Entity>): Promise<void> {
    await this.initializeCache();
    await this.durable.deleteSearch(criteria);
    await this.cache.deleteSearch(criteria);
  }

  /**
   * Runs `fn` inside the durable store's transaction. The cache layer is
   * intentionally bypassed for the transaction's duration — coordinating
   * two-phase commit between the durable store and an in-memory cache is
   * out of scope, and callers asking for `withTransaction` are asking for
   * atomicity, which only the durable can provide. Inside `fn`, reads and
   * writes go straight through the durable's transaction handle.
   *
   * **`tx` identity:** the handle passed to `fn` is the durable store's
   * transaction proxy, not a `CachedTabularStorage`, despite the `(tx: this)`
   * type. Wrapper-specific members like `tx.cache` or `tx.invalidateCache()`
   * therefore type-check but throw at runtime — call them on the original
   * wrapper after `withTransaction` resolves instead.
   *
   * **Cache freshness on commit/rollback:** any rows the transaction touched
   * (committed inserts/updates/deletes, or rolled-back attempts that were
   * already mirrored into the cache via earlier non-tx writes) leave the
   * cache potentially out of step with durable. Invalidate unconditionally
   * after `fn` resolves so the next read repopulates the cache from durable
   * — over-invalidating on rollback is cheap and avoids leaking the
   * rollback boundary into the cache layer. Inheriting `BaseTabularStorage`'s
   * no-op default here would silently lose the rollback / atomicity guarantee,
   * since the default just runs `fn(this)` against the cached wrapper itself.
   */
  override async withTransaction<T>(fn: (tx: this) => Promise<T>): Promise<T> {
    await this.initializeCache();
    try {
      return await this.durable.withTransaction(
        fn as unknown as (
          tx: ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>
        ) => Promise<T>
      );
    } finally {
      // Conservative: forget what we cached and let subsequent reads re-warm
      // from durable. `invalidateCache()` is a deleteAll on the cache plus a
      // reset of the init flag, so the next read goes through `initializeCache`
      // and rebuilds from the durable's post-commit state.
      await this.invalidateCache();
    }
  }

  async invalidateCache(): Promise<void> {
    await this.cache.deleteAll();
    this.cacheInitialized = false;
  }

  async refreshCache(): Promise<void> {
    await this.cache.deleteAll();
    this.cacheInitialized = false;
    await this.initializeCache();
  }

  /**
   * Subscribes to durable changes (including external ones) and keeps the
   * cache in step before forwarding each change to `callback`.
   */
  override subscribeToChanges(
    callback: (change: any) => void,
    options?: TabularSubscribeOptions
  ): () => void {
    return this.durable.subscribeToChanges(async (change) => {
      if (change.type === "INSERT" || change.type === "UPDATE") {
        if (change.new) {
          await this.cache.put(change.new);
        }
      } else if (change.type === "DELETE") {
        if (change.old) {
          await this.cache.delete(change.old);
        }
      }

      callback(change);
    }, options);
  }

  /**
   * Forwards `setupDatabase` to the durable storage (which owns any
   * declared tabular migrations) and to the cache. Migrations themselves
   * are passed when constructing the inner durable storage; the wrapper
   * does not declare its own.
   */
  override async setupDatabase(): Promise<void> {
    await this.durable.setupDatabase();
    await this.cache.setupDatabase();
  }

  /**
   * Delegates to the durable storage's applier so callers that use the
   * wrapper observe migrations declared on the inner durable layer.
   */
  public override getMigrationApplier(): ITabularMigrationApplier | null {
    const inner = this.durable as unknown as {
      getMigrationApplier?: () => ITabularMigrationApplier | null;
    };
    return inner.getMigrationApplier?.() ?? null;
  }

  override destroy(): void {
    this.durable.destroy();
    this.cache.destroy();
  }
}
